import React, { useEffect, useMemo, useState } from 'react';
import {
  PhotoCameraOutlined,
  RefreshOutlined,
  ContentCopyOutlined,
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  LinkOutlined,
} from '@mui/icons-material';
import { getAllAccounts, AccountData } from '../../services/userManagementService';
import {
  listSnapshots,
  getSnapshotById,
  captureSnapshot,
  AccountSnapshot,
  SnapshotListItem,
} from '../../services/snapshotService';

// ─── Helpers ─────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function shortHash(h: string): string {
  return h.slice(0, 10);
}

const SECTION_LABELS: Record<string, string> = {
  account_formulas: 'Strategies',
  account_settings: 'Account Settings',
  notification_rules: 'Notification Rules',
  hedge_allocation_templates: 'Hedge Allocation Templates',
  material_mappings: 'Material Mappings',
  demand_positions: 'Demand Positions',
  supply_positions: 'Supply Positions',
  hedge_positions: 'Hedge Positions',
};

function formatSize(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

// ─── JSON Tree Renderer ──────────────────────────────────────────

const JSON_COLORS = {
  key: '#7c3aed',        // purple
  string: '#15803d',     // green
  number: '#c2410c',     // orange
  boolean: '#0369a1',    // blue
  null: '#6b7280',       // gray
  bracket: '#374151',    // dark gray
  comma: '#9ca3af',      // light gray
  preview: '#9ca3af',    // preview for collapsed
};

interface JsonNodeProps {
  value: any;
  depth?: number;
  defaultExpanded?: boolean;
  /** Label (object key or array index) shown before the value */
  label?: string | number;
  /** Whether to show a trailing comma */
  trailingComma?: boolean;
}

const JsonNode: React.FC<JsonNodeProps> = ({
  value,
  depth = 0,
  defaultExpanded = false,
  label,
  trailingComma = false,
}) => {
  // Hook must be called unconditionally (React rules-of-hooks).
  // It's only actually used for object/array branches below.
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Primitives & null
  if (value === null) {
    return (
      <span style={{ whiteSpace: 'pre' }}>
        {label !== undefined && (
          <>
            <span style={{ color: JSON_COLORS.key }}>
              {typeof label === 'string' ? `"${label}"` : label}
            </span>
            <span style={{ color: JSON_COLORS.bracket }}>: </span>
          </>
        )}
        <span style={{ color: JSON_COLORS.null, fontStyle: 'italic' }}>null</span>
        {trailingComma && <span style={{ color: JSON_COLORS.comma }}>,</span>}
      </span>
    );
  }
  if (typeof value !== 'object') {
    let display: string;
    let color: string;
    if (typeof value === 'string') {
      display = `"${value}"`;
      color = JSON_COLORS.string;
    } else if (typeof value === 'number') {
      display = String(value);
      color = JSON_COLORS.number;
    } else if (typeof value === 'boolean') {
      display = String(value);
      color = JSON_COLORS.boolean;
    } else {
      display = String(value);
      color = '#111827';
    }
    return (
      <span style={{ whiteSpace: 'pre' }}>
        {label !== undefined && (
          <>
            <span style={{ color: JSON_COLORS.key }}>
              {typeof label === 'string' ? `"${label}"` : label}
            </span>
            <span style={{ color: JSON_COLORS.bracket }}>: </span>
          </>
        )}
        <span style={{ color }}>{display}</span>
        {trailingComma && <span style={{ color: JSON_COLORS.comma }}>,</span>}
      </span>
    );
  }

  // Object or Array
  const isArray = Array.isArray(value);
  const entries = isArray
    ? value.map((v: any, i: number) => [i, v] as [number, any])
    : Object.entries(value);
  const open = isArray ? '[' : '{';
  const close = isArray ? ']' : '}';

  if (entries.length === 0) {
    return (
      <span style={{ whiteSpace: 'pre' }}>
        {label !== undefined && (
          <>
            <span style={{ color: JSON_COLORS.key }}>
              {typeof label === 'string' ? `"${label}"` : label}
            </span>
            <span style={{ color: JSON_COLORS.bracket }}>: </span>
          </>
        )}
        <span style={{ color: JSON_COLORS.bracket }}>{open}{close}</span>
        {trailingComma && <span style={{ color: JSON_COLORS.comma }}>,</span>}
      </span>
    );
  }

  // Preview line summary when collapsed
  let preview = '';
  if (isArray) {
    preview = `${entries.length} item${entries.length === 1 ? '' : 's'}`;
  } else {
    const keys = entries.slice(0, 3).map(([k]) => String(k));
    preview = entries.length <= 3
      ? keys.join(', ')
      : `${keys.join(', ')}, …+${entries.length - 3}`;
  }

  const indent = '  '.repeat(depth);
  const childIndent = '  '.repeat(depth + 1);

  return (
    <span style={{ whiteSpace: 'pre' }}>
      {label !== undefined && (
        <>
          <span style={{ color: JSON_COLORS.key }}>
            {typeof label === 'string' ? `"${label}"` : label}
          </span>
          <span style={{ color: JSON_COLORS.bracket }}>: </span>
        </>
      )}
      <span
        onClick={() => setExpanded(!expanded)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          color: JSON_COLORS.bracket,
        }}
        title={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? '▾' : '▸'} {open}
      </span>
      {!expanded && (
        <>
          <span style={{ color: JSON_COLORS.preview, fontStyle: 'italic' }}> {preview} </span>
          <span style={{ color: JSON_COLORS.bracket }}>{close}</span>
        </>
      )}
      {expanded && (
        <>
          {'\n'}
          {entries.map(([key, childValue], i) => (
            <span key={String(key)}>
              {childIndent}
              <JsonNode
                value={childValue}
                depth={depth + 1}
                defaultExpanded={false}
                label={key}
                trailingComma={i < entries.length - 1}
              />
              {'\n'}
            </span>
          ))}
          {indent}
          <span style={{ color: JSON_COLORS.bracket }}>{close}</span>
        </>
      )}
      {trailingComma && <span style={{ color: JSON_COLORS.comma }}>,</span>}
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────

const AccountSnapshots: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotListItem[]>([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState<AccountSnapshot | null>(null);

  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Load accounts on mount
  useEffect(() => {
    (async () => {
      try {
        const list = await getAllAccounts();
        setAccounts(list);
        if (list.length > 0) setAccountId(Number(list[0].id));
      } catch (e: any) {
        setError(e.message || 'Failed to load accounts');
      } finally {
        setLoadingAccounts(false);
      }
    })();
  }, []);

  // Reload snapshots when account changes
  useEffect(() => {
    if (accountId == null) return;
    refreshList();
  }, [accountId]);

  async function refreshList() {
    if (accountId == null) return;
    setLoadingList(true);
    setError(null);
    try {
      const items = await listSnapshots(accountId, { limit: 100 });
      setSnapshots(items);
      setSelectedSnapshot(null);
    } catch (e: any) {
      setError(e.message || 'Failed to list snapshots');
    } finally {
      setLoadingList(false);
    }
  }

  async function loadSnapshotDetail(item: SnapshotListItem) {
    if (accountId == null) return;
    setLoadingDetail(true);
    setError(null);
    try {
      // Fetch by id, not by date — multiple snapshots can share a date
      // and a date-based lookup would always resolve to the latest one.
      const snap = await getSnapshotById(accountId, item.id);
      setSelectedSnapshot(snap);
      // Default: collapse all sections
      setExpandedSections({});
    } catch (e: any) {
      setError(e.message || 'Failed to fetch snapshot');
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCapture() {
    if (accountId == null) return;
    setCapturing(true);
    setError(null);
    setInfo(null);
    try {
      const r = await captureSnapshot(accountId);
      setInfo(
        r.deduped
          ? `Captured snapshot #${r.snapshotId} — identical to previous (deduped, points to earlier row).`
          : `Captured snapshot #${r.snapshotId} — new state stored.`,
      );
      await refreshList();
    } catch (e: any) {
      setError(e.message || 'Capture failed');
    } finally {
      setCapturing(false);
    }
  }

  function toggleSection(key: string) {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function copyJson(value: any) {
    try {
      navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      setInfo('Copied JSON to clipboard.');
      setTimeout(() => setInfo(null), 2000);
    } catch {
      // ignore
    }
  }

  const sectionEntries = useMemo(() => {
    if (!selectedSnapshot?.data) return [];
    return Object.entries(SECTION_LABELS).map(([key, label]) => {
      const value = (selectedSnapshot.data as any)[key];
      const count = Array.isArray(value) ? value.length : value ? 1 : 0;
      return { key, label, value, count };
    });
  }, [selectedSnapshot]);

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className="px-4 py-3">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-left" style={{ color: '#111827' }}>
          Account Snapshots
        </h1>
        <p className="text-xs mt-0.5 text-left" style={{ color: '#6b7280' }}>
          Daily point-in-time snapshots of all mutable account state (strategies, positions, rules, settings)
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
            Account
          </label>
          <select
            disabled={loadingAccounts}
            value={accountId ?? ''}
            onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
            className="text-sm rounded-md px-3 py-2"
            style={{ border: '1px solid #d1d5db', minWidth: 240 }}
          >
            {loadingAccounts && <option>Loading…</option>}
            {!loadingAccounts &&
              accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} (#{a.id})
                </option>
              ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCapture}
            disabled={capturing || accountId == null}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium"
            style={{
              background: capturing ? '#93c5fd' : '#3b82f6',
              color: '#ffffff',
              cursor: capturing ? 'wait' : 'pointer',
              border: 'none',
            }}
          >
            <PhotoCameraOutlined sx={{ fontSize: 18 }} />
            {capturing ? 'Capturing…' : 'Capture Now'}
          </button>
          <button
            type="button"
            onClick={refreshList}
            disabled={loadingList || accountId == null}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium"
            style={{
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              cursor: loadingList ? 'wait' : 'pointer',
            }}
          >
            <RefreshOutlined sx={{ fontSize: 18 }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div
          className="mb-3 px-3 py-2 rounded-md text-sm"
          style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c' }}
        >
          {error}
        </div>
      )}
      {info && (
        <div
          className="mb-3 px-3 py-2 rounded-md text-sm flex items-center gap-2"
          style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d' }}
        >
          <CheckCircleOutline sx={{ fontSize: 18 }} />
          {info}
        </div>
      )}

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* List */}
        <div
          className="bg-white rounded-lg overflow-hidden"
          style={{ border: '1px solid #e5e7eb' }}
        >
          <div
            className="px-3 py-2 text-xs font-semibold"
            style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#374151' }}
          >
            Snapshots ({snapshots.length})
          </div>
          {loadingList && (
            <div className="px-3 py-4 text-sm" style={{ color: '#6b7280' }}>
              Loading…
            </div>
          )}
          {!loadingList && snapshots.length === 0 && (
            <div className="px-3 py-4 text-sm" style={{ color: '#6b7280' }}>
              No snapshots yet. Click &quot;Capture Now&quot; to create the first one.
            </div>
          )}
          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            {snapshots.map((s) => {
              const isSelected = selectedSnapshot?.id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => loadSnapshotDetail(s)}
                  className="w-full text-left px-3 py-2 transition-colors"
                  style={{
                    background: isSelected ? '#eff6ff' : '#ffffff',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium" style={{ color: '#111827' }}>
                      {s.snapshotDate}
                    </div>
                    {s.deduped && (
                      <span
                        title="Identical to previous — stored as pointer"
                        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                        style={{ background: '#f3f4f6', color: '#6b7280' }}
                      >
                        <LinkOutlined sx={{ fontSize: 12 }} />
                        deduped
                      </span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                    {formatDateTime(s.createdAt)} · #{s.id} · {formatSize(s.sizeBytes)} · {shortHash(s.dataHash)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div
          className="bg-white rounded-lg"
          style={{ border: '1px solid #e5e7eb', minHeight: 400 }}
        >
          {loadingDetail && (
            <div className="px-4 py-6 text-sm" style={{ color: '#6b7280' }}>
              Loading snapshot…
            </div>
          )}

          {!loadingDetail && !selectedSnapshot && (
            <div className="px-4 py-6 text-sm" style={{ color: '#6b7280' }}>
              Select a snapshot from the list to view its contents.
            </div>
          )}

          {!loadingDetail && selectedSnapshot && (
            <>
              <div
                className="px-4 py-3 flex items-start justify-between"
                style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}
              >
                <div>
                  <div className="text-sm font-semibold" style={{ color: '#111827' }}>
                    Snapshot #{selectedSnapshot.id} — {selectedSnapshot.snapshotDate}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                    Captured {formatDateTime(selectedSnapshot.createdAt)}
                    {' · '}
                    Hash: <code title={selectedSnapshot.dataHash} style={{ fontFamily: 'monospace' }}>{shortHash(selectedSnapshot.dataHash)}</code>
                    {selectedSnapshot.previousSnapshotId !== null && (
                      <> · Pointer to #{selectedSnapshot.previousSnapshotId}</>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyJson(selectedSnapshot.data)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs"
                  style={{ background: '#ffffff', border: '1px solid #d1d5db', color: '#374151' }}
                >
                  <ContentCopyOutlined sx={{ fontSize: 14 }} />
                  Copy full JSON
                </button>
              </div>

              <div className="p-3">
                {!selectedSnapshot.data && (
                  <div className="text-sm px-2 py-3" style={{ color: '#6b7280' }}>
                    No payload returned (snapshot pointer could not be resolved).
                  </div>
                )}
                {selectedSnapshot.data &&
                  sectionEntries.map(({ key, label, value, count }) => {
                    const expanded = !!expandedSections[key];
                    const isArray = Array.isArray(value);
                    return (
                      <div
                        key={key}
                        className="mb-2 rounded-md overflow-hidden"
                        style={{ border: '1px solid #e5e7eb' }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSection(key)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left"
                          style={{ background: '#f9fafb', cursor: 'pointer' }}
                        >
                          <div className="flex items-center gap-2">
                            {expanded ? (
                              <ExpandLess sx={{ fontSize: 18, color: '#6b7280' }} />
                            ) : (
                              <ExpandMore sx={{ fontSize: 18, color: '#6b7280' }} />
                            )}
                            <span className="text-sm font-medium" style={{ color: '#111827' }}>
                              {label}
                            </span>
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: '#e5e7eb', color: '#374151' }}
                            >
                              {isArray ? `${count} rows` : value ? '1 obj' : 'empty'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyJson(value);
                            }}
                            className="text-xs px-2 py-1 rounded"
                            style={{ background: '#ffffff', border: '1px solid #d1d5db', color: '#374151' }}
                          >
                            <ContentCopyOutlined sx={{ fontSize: 12, marginRight: 4 }} />
                            copy
                          </button>
                        </button>
                        {expanded && (
                          <div
                            className="text-xs p-3 overflow-auto"
                            style={{
                              background: '#f8fafc',
                              color: '#111827',
                              maxHeight: 420,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              margin: 0,
                              borderTop: '1px solid #e5e7eb',
                              lineHeight: 1.55,
                              whiteSpace: 'pre',
                            }}
                          >
                            <JsonNode value={value} defaultExpanded={true} />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSnapshots;
